import { AuthProviderForm, GenericCredentials } from '../../sessions.service';
import { OAuth2AuthProviderConfig, OAuth2GrantType } from '../oauth2.provider';
import * as uuid from 'uuid';

/**
 * Implementation of the Authorization Code OAuth2 grant type.
 * This class handles the authorization code flow for OAuth2 authentication.
 */
export class AuthorizationCodeOAuth2GrantType implements OAuth2GrantType {
  /**
   * Returns an empty array of sign-in fields, as this grant type does not require any additional fields.
   * @returns An empty array of sign-in fields.
   */
  getSignInFields(): AuthProviderForm {
    return [];
  }

  /**
   * Prepares the authentication payload for the authorization code flow.
   * This method obtains an authorization code and builds the payload for the token request.
   * @param _ The generic credentials (not used in this implementation).
   * @param config The OAuth2 authentication provider configuration.
   * @returns A promise that resolves to the authentication payload.
   */
  public async prepareAuthenticationPayload(
    _: GenericCredentials,
    config: OAuth2AuthProviderConfig
  ): Promise<BodyInit> {
    const authorizationCode = await this.obtainAuthorizationCode(config);

    const formData = this.buildBody(authorizationCode, config);

    return formData;
  }

  /**
   * Obtains an authorization code by launching the OAuth2 authorization URL in a new window.
   * This method waits for the authorization code to be set from the OAuth2 flow.
   * @param config The OAuth2 authentication provider configuration.
   * @returns A promise that resolves to the obtained authorization code.
   */
  async obtainAuthorizationCode(config: OAuth2AuthProviderConfig): Promise<string> {
    const authorizationId = uuid.v4();

    const urlParameters = new URLSearchParams();
    urlParameters.set('client_id', config.clientId);
    urlParameters.set('redirect_uri', config.redirectUrl);
    urlParameters.set('response_type', 'code');
    urlParameters.set('scope', config.scopes.join(' '));
    urlParameters.set('state', authorizationId);

    // Launch the login page
    const windowHandle = open(`${config.authorizationUrl}?${urlParameters.toString()}`, 'OAuth2SignIn');

    if (!windowHandle) {
      console.error('Failed to open OAuth2 signin window');
      throw new Error('Failed to open OAuth2 signin window');
    }

    //Now wait until the authorization code is set from the oauth2 flow
    const authorizationCode = await this.waitForAuthorizationCode(
      authorizationId,
      windowHandle,
      config.authorizationCodeTimeoutSeconds
    );

    return authorizationCode;
  }

  /**
   * Waits for the authorization code to be set from the OAuth2 flow.
   * This method listens for a message on the BroadcastChannel and resolves when the authorization code is received.
   * @param authorizationId The ID of the authorization request.
   * @param windowHandle The handle of the window that launched the OAuth2 authorization URL.
   * @param timeoutSeconds The timeout in seconds (default: 120).
   * @returns A promise that resolves to the obtained authorization code.
   */
  private async waitForAuthorizationCode(
    authorizationId: string,
    windowHandle: Window,
    timeoutSeconds: number = 120
  ): Promise<string> {
    let checkTimeout: ReturnType<typeof setInterval> | null = null;

    return new Promise<string>((resolve, reject) => {
      checkTimeout = setInterval(() => {
        windowHandle.close();
        reject('Timeout');
      }, timeoutSeconds * 1000);

      const oauth2CallbackChannel = new BroadcastChannel(`oauth2-callback`);
      oauth2CallbackChannel.onmessage = ((message: any) => {
        if (message.data.authorizationId !== authorizationId) {
          return;
        }

        if (message.data.error) {
          reject(message.data.error);
        }

        if (message.data.code) {
          resolve(message.data.code);
        }
      }).bind(oauth2CallbackChannel);
    }).finally(() => {
      if (checkTimeout! !== null) {
        clearTimeout(checkTimeout!);
      }
    });
  }

  /**
   * Builds the payload for the token request.
   * @param authorizationCode The obtained authorization code.
   * @param config The OAuth2 authentication provider configuration.
   * @returns The payload for the token request.
   */
  buildBody(authorizationCode: string, config: OAuth2AuthProviderConfig): URLSearchParams {
    const formData = new URLSearchParams();
    formData.append('grant_type', 'authorization_code');
    formData.append('code', authorizationCode);
    formData.append('client_id', config.clientId);
    formData.append('redirect_uri', config.redirectUrl);
    formData.append('scope', config.scopes.join(' '));
    return formData;
  }
}
